/**
 * QuotaIndicator Component
 *
 * Displays file count and total size quota with progress bars (Coss Progress).
 * Shows warning/error states when quota is near or at limit. Colors are driven
 * by design tokens (primary/warning/destructive), not hard-coded palette values.
 */

import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { QuotaInfo } from "@/types";
import { Progress, ProgressIndicator, ProgressTrack } from "@/components/ui/progress";
import {
  formatFileSize,
  getQuotaPercentage,
  getQuotaState,
} from "@/features/knowledge-base/knowledge-base";

interface QuotaIndicatorProps {
  quota: QuotaInfo;
}

export function QuotaIndicator({ quota }: QuotaIndicatorProps) {
  const { t } = useTranslation("kb");
  const fileLabelId = useId();
  const sizeLabelId = useId();
  const state = getQuotaState(quota);
  const filePercentage = getQuotaPercentage(quota.fileCount, quota.maxFiles);
  const sizePercentage = getQuotaPercentage(quota.totalSize, quota.maxSize);

  const indicatorColors = {
    normal: "bg-primary",
    warning: "bg-warning",
    error: "bg-destructive",
  };

  const stateTextColors = {
    normal: "text-[var(--foreground)]",
    warning: "text-warning",
    error: "text-destructive",
  };

  const indicatorColor = indicatorColors[state];
  const textColor = stateTextColors[state];

  return (
    <div className="mb-6" data-test="quota-indicator">
      {/* Quota summary */}
      <div className={`text-sm font-medium mb-2 ${textColor}`}>
        {quota.fileCount} / {quota.maxFiles} {t("quota.files")} •{" "}
        {formatFileSize(quota.totalSize)} / {formatFileSize(quota.maxSize)}
      </div>

      {/* File count progress bar */}
      <div className="mb-2">
        <div className="flex justify-between text-xs text-[var(--muted-foreground)] mb-1">
          <span id={fileLabelId}>{t("quota.filesLabel")}</span>
          <span>{filePercentage}%</span>
        </div>
        <Progress value={filePercentage} aria-labelledby={fileLabelId}>
          <ProgressTrack className="h-2 bg-[var(--secondary)]">
            <ProgressIndicator className={indicatorColor} />
          </ProgressTrack>
        </Progress>
      </div>

      {/* Size progress bar */}
      <div>
        <div className="flex justify-between text-xs text-[var(--muted-foreground)] mb-1">
          <span id={sizeLabelId}>{t("quota.storageLabel")}</span>
          <span>{sizePercentage}%</span>
        </div>
        <Progress value={sizePercentage} aria-labelledby={sizeLabelId}>
          <ProgressTrack className="h-2 bg-[var(--secondary)]">
            <ProgressIndicator className={indicatorColor} />
          </ProgressTrack>
        </Progress>
      </div>

      {/* Warning message */}
      {state === "warning" && (
        <div className="mt-2 text-xs text-warning bg-warning/10 px-2 py-1 rounded">
          {t("quota.warning")}
        </div>
      )}

      {/* Error message */}
      {state === "error" && (
        <div className="mt-2 text-xs text-destructive bg-destructive/10 px-2 py-1 rounded">
          {t("quota.error")}
        </div>
      )}
    </div>
  );
}
